import {Dialog as LumaAccessibleDialog, DialogPanel as LumaAccessiblePanel, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
// ModalCerrarSesion.tsx — Co Marca — Cerrar Sesión POS
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Lock, AlertTriangle, CreditCard } from 'lucide-react';

export interface SessionPaymentTypeSummary {
    id: number;
    nombre: string;
    categoria: 'efectivo' | 'banco' | 'otro';
    cantidad: number;
    monto: string;
}

interface ModalCerrarSesionProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (saldoFinal: number, observaciones: string) => void;
    saldoActual: number;
    isLoading: boolean;
    tiposPago?: SessionPaymentTypeSummary[];
    totalPagos?: string;
}

const C = {
    primary:        'var(--ad-primary, #3564db)',
    primaryDark:    'var(--ad-primary-dark, #274f9d)',
    secondary:      'var(--ad-secondary, #3564db)',
    secondaryDark:  '#004d7a',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accentDark:     '#EC8400',
};

const css = `

.mcs-modal * { font-family:'DM Sans',sans-serif; box-sizing:border-box; }
@keyframes mcsFadeIn  { from{opacity:0;} to{opacity:1;} }
@keyframes mcsSlideUp { from{opacity:0;transform:translateY(20px) scale(.97);} to{opacity:1;transform:translateY(0) scale(1);} }
@keyframes mcsSpin { to{transform:rotate(360deg);} }
.mcs-overlay { animation:mcsFadeIn .2s ease both; }
.mcs-dialog  { display:flex; flex-direction:column; max-height:calc(100dvh - 32px); animation:mcsSlideUp .3s cubic-bezier(.16,1,.3,1) both; }
.mcs-input {
    width:100%; padding:10px 14px; border-radius:10px;
    border:1.5px solid var(--ad-line, #e5e7eb); background:#fafafa;
    font-size:.88rem; color:var(--ad-ink, #374151); outline:none; transition:all .2s;
    font-family:'DM Sans',sans-serif;
}
.mcs-input:focus { border-color:${C.secondary}; box-shadow:0 0 0 3px color-mix(in srgb, ${C.secondary} 7.06%, transparent); background:var(--ad-surface, #fff); }
.mcs-input:read-only { background:var(--ad-line, #f1f5f9); color:var(--ad-muted, #94a3b8); cursor:not-allowed; }
.mcs-textarea {
    width:100%; padding:10px 14px; border-radius:10px;
    border:1.5px solid var(--ad-line, #e5e7eb); background:#fafafa;
    font-size:.88rem; color:var(--ad-ink, #374151); outline:none;
    transition:all .2s; resize:none; min-height:90px;
    font-family:'DM Sans',sans-serif;
}
.mcs-textarea:focus { border-color:${C.secondary}; box-shadow:0 0 0 3px color-mix(in srgb, ${C.secondary} 7.06%, transparent); background:var(--ad-surface, #fff); }
.mcs-btn-confirm {
    display:inline-flex;align-items:center;justify-content:center;gap:7px;
    background:linear-gradient(135deg,${C.primary},${C.primaryDark});
    color:#fff;border:none;border-radius:11px;
    padding:10px 22px;font-size:.88rem;font-weight:800;
    cursor:pointer;box-shadow:0 4px 14px rgba(var(--ad-primary-rgb, 193, 52, 139),.28);
    transition:all .22s cubic-bezier(.16,1,.3,1);min-width:130px;
    font-family:'DM Sans',sans-serif;
}
.mcs-btn-confirm:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 8px 22px rgba(var(--ad-primary-rgb, 193, 52, 139),.36); }
.mcs-btn-confirm:disabled { background:var(--ad-line, #e2e8f0); color:var(--ad-muted, #94a3b8); box-shadow:none; cursor:not-allowed; }
.mcs-btn-ghost {
    display:inline-flex;align-items:center;gap:7px;
    background:var(--ad-soft, #f8fafc);border:1.5px solid var(--ad-line, #e2e8f0);
    color:var(--ad-ink, #374151);border-radius:11px;
    padding:10px 18px;font-size:.88rem;font-weight:700;
    cursor:pointer;transition:all .2s;
    font-family:'DM Sans',sans-serif;
}
.mcs-btn-ghost:hover:not(:disabled) { background:var(--ad-line, #f1f5f9); }
.mcs-btn-ghost:disabled { opacity:.5; cursor:not-allowed; }

.mcs-body { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:20px; overflow-y:auto; min-height:0; }
.mcs-body > * { min-width:0; }
.mcs-body > :first-child { grid-column:1; grid-row:1; }
.mcs-body > section { grid-column:2; grid-row:1 / span 2; }
.mcs-body > :last-child { grid-column:1; grid-row:2; }
@media(max-width:640px) {
 .mcs-body { grid-template-columns:minmax(0,1fr); gap:14px; }
 .mcs-body > * { grid-column:1 !important; grid-row:auto !important; }
 .mcs-footer { flex-wrap:wrap; }
 .mcs-footer button { flex:1; justify-content:center; }
}
`;

const ModalCerrarSesion: React.FC<ModalCerrarSesionProps> = ({
    isOpen, onClose, onConfirm, saldoActual, isLoading, tiposPago = [], totalPagos = '0.00'
}) => {
    const [saldoFinal, setSaldoFinal]       = useState('');
    const [observaciones, setObservaciones] = useState('');

    useEffect(() => {
        if (isOpen) setSaldoFinal(saldoActual.toFixed(2));
    }, [isOpen, saldoActual]);

    const handleConfirm = () => {
        onConfirm(parseFloat(saldoFinal), observaciones);
        setObservaciones('');
    };

    const categoriasPago = (['efectivo', 'banco', 'otro'] as const).map(categoria => {
        const medios = tiposPago.filter(tipo => tipo.categoria === categoria);
        return {
            categoria,
            etiqueta: categoria === 'efectivo' ? 'Efectivo' : categoria === 'banco' ? 'Banco' : 'Otros',
            cantidad: medios.reduce((total, tipo) => total + tipo.cantidad, 0),
            monto: medios.reduce((total, tipo) => total + Number(tipo.monto), 0),
        };
    });

    if (!isOpen) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="mcs-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                <div className="mcs-overlay" onClick={() => { if (!isLoading) onClose(); }} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.55)', backdropFilter:'blur(6px)' }}/>

                <LumaAccessiblePanel data-luma-dialog="true" role="dialog" aria-modal="true" aria-labelledby="mcs-title" className="mcs-dialog card custom-card vx-card" style={{ position:'relative', background:'var(--ad-surface, #fff)', borderRadius:'22px', width:'100%', maxWidth:'860px', boxShadow:'0 32px 80px rgba(0,0,0,.2)', border:'1.5px solid var(--ad-line, #f1f5f9)', overflow:'hidden' }}>

                    {/* Barra top — rojo para cierre */}
                    <div style={{ height:'4px', background:`linear-gradient(90deg,${C.primary},${C.primaryDark},color-mix(in srgb, ${C.primary} 50.2%, transparent))` }}/>

                    {/* Header */}
                    <div style={{ padding:'22px 24px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                            <div style={{ width:'40px', height:'40px', borderRadius:'13px', background:`color-mix(in srgb, ${C.primary} 6.27%, transparent)`, border:`1.5px solid color-mix(in srgb, ${C.primary} 13.33%, transparent)`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }} className="">
                                <AlertTriangle size={19} color={C.primary}/>
                            </div>
                            <div>
                                <LumaAccessibleTitle id="mcs-title" style={{ fontSize:'1.05rem', fontWeight:'650', color:'var(--ad-ink, #0f172a)', letterSpacing:'-.02em' }}>Cerrar Sesión</LumaAccessibleTitle>
                                <p style={{ fontSize:'.72rem', color:'var(--ad-muted, #94a3b8)', marginTop:'2px' }}>Confirma el cierre de la sesión actual</p>
                            </div>
                        </div>
                        <button onClick={onClose} disabled={isLoading}
                            style={{ width:'30px', height:'30px', borderRadius:'9px', border:'1.5px solid var(--ad-line, #f1f5f9)', background:'var(--ad-soft, #f8fafc)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}
                            onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='var(--ad-line, #f1f5f9)'}
                            onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='var(--ad-soft, #f8fafc)'}>
                            <X size={14} color="var(--ad-muted, #64748b)"/>
                        </button>
                    </div>

                    {/* Barra de colores decorativa */}
                    <div style={{ display:'flex', gap:'4px', padding:'12px 24px 0' }}>
                        {[C.primary,C.secondary,C.accentDark,C.secondaryLight].map((c,i)=>(
                            <div key={i} style={{ height:'2px', width:i===0?'16px':'5px', borderRadius:'2px', background:c, opacity:.5 }}/>
                        ))}
                    </div>

                    {/* Form */}
                    <div className="mcs-body" style={{ padding:'20px 24px' }}>

                        {/* Saldo final (readonly) */}
                        <div>
                            <label style={{ display:'block', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                Saldo Final (Bs.) *
                            </label>
                            <div style={{ position:'relative' }}>
                                <input type="number" value={saldoFinal} readOnly className="mcs-input"
                                    style={{ paddingRight:'40px' }} placeholder="0.00" step="0.01" min="0"/>
                                <div style={{ position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}>
                                    <Lock size={14} color="var(--ad-muted, #94a3b8)"/>
                                </div>
                            </div>

                            {/* Card saldo actual */}
                            <div style={{ marginTop:'8px', padding:'10px 14px', background:`color-mix(in srgb, ${C.secondaryLight} 3.92%, transparent)`, border:`1.5px solid color-mix(in srgb, ${C.secondaryLight} 15.69%, transparent)`, borderRadius:'10px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                <div>
                                    <p style={{ fontSize:'.72rem', fontWeight:'700', color:'var(--ad-success, #065f46)' }}>Saldo actual registrado</p>
                                    <p style={{ fontSize:'.68rem', color:'var(--ad-muted, #94a3b8)', marginTop:'2px' }}>No puede modificarse para garantizar la integridad</p>
                                </div>
                                <div style={{ textAlign:'right', flexShrink:0 }}>
                                    <span style={{ fontSize:'1.1rem', fontWeight:'650', color:'var(--ad-ink, #0f172a)' }}>{saldoActual.toFixed(2)}</span>
                                    <span style={{ fontSize:'.72rem', color:'var(--ad-muted, #94a3b8)', marginLeft:'3px' }}>Bs.</span>
                                </div>
                            </div>
                        </div>

                        <section aria-labelledby="mcs-payment-types" style={{ border:'1.5px solid var(--ad-line, #e2e8f0)', borderRadius:'14px', overflow:'hidden' }}>
                            <div style={{ padding:'10px 12px', background:'var(--ad-soft, #f8fafc)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px' }}>
                                <h4 id="mcs-payment-types" style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.76rem', fontWeight:900, color:'var(--ad-ink, #334155)', margin:0 }}><CreditCard size={13} color={C.secondary}/> Tipos de Pago</h4>
                                <strong style={{ fontSize:'.8rem', color:C.secondaryDark }}>{Number(totalPagos).toFixed(2)} Bs.</strong>
                            </div>
                            <div style={{ padding:'4px 12px 8px' }}>
                                {categoriasPago.map(item => (
                                    <div key={item.categoria} style={{ display:'grid', gridTemplateColumns:'1fr auto auto', alignItems:'center', gap:'10px', padding:'8px 0', borderTop:'1px solid var(--ad-line, #f1f5f9)' }}>
                                        <span style={{ fontSize:'.74rem', fontWeight:800, color:'var(--ad-ink, #334155)' }}>{item.etiqueta}</span>
                                        <span style={{ fontSize:'.68rem', color:'var(--ad-muted, #64748b)' }}>{item.cantidad} {item.cantidad === 1 ? 'pago' : 'pagos'}</span>
                                        <strong style={{ fontSize:'.74rem', color:'var(--ad-ink, #0f172a)' }}>{item.monto.toFixed(2)} Bs.</strong>
                                    </div>
                                ))}
                                {tiposPago.length > 0 && <div style={{ paddingTop:'8px', borderTop:'1px dashed #cbd5e1' }}>
                                    <p style={{ margin:'0 0 5px', fontSize:'.65rem', fontWeight:900, color:'var(--ad-muted, #64748b)', textTransform:'uppercase', letterSpacing:'.06em' }}>Medios específicos</p>
                                    {tiposPago.map(tipo => (
                                        <p key={tipo.id} style={{ display:'flex', justifyContent:'space-between', gap:'8px', margin:'3px 0', fontSize:'.68rem', color:'var(--ad-muted, #64748b)' }}>
                                            <span>{tipo.nombre} · {tipo.cantidad}</span><span>{Number(tipo.monto).toFixed(2)} Bs.</span>
                                        </p>
                                    ))}
                                </div>}
                            </div>
                        </section>

                        {/* Observaciones */}
                        <div>
                            <label style={{ display:'block', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                Observaciones de cierre
                            </label>
                            <textarea value={observaciones} onChange={e=>setObservaciones(e.target.value)} className="mcs-textarea"
                                placeholder="Observaciones sobre diferencias, errores o notas importantes..."/>
                            <p style={{ fontSize:'.68rem', color:'var(--ad-muted, #94a3b8)', marginTop:'5px' }}>Opcional — incluye notas sobre el estado final de la caja</p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="mcs-footer" style={{ padding:'14px 24px 22px', display:'flex', justifyContent:'flex-end', gap:'10px', borderTop:'1.5px solid var(--ad-soft, #f8fafc)', flexShrink:0 }}>
                        <button onClick={()=>{ onClose(); setObservaciones(''); }} disabled={isLoading} className="mcs-btn-ghost">
                            Cancelar
                        </button>
                        <button onClick={handleConfirm} disabled={!saldoFinal||isLoading} className="mcs-btn-confirm">
                            {isLoading
                                ? <><span style={{ width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'mcsSpin .7s linear infinite',display:'inline-block' }}/> Cerrando...</>
                                : 'Confirmar Cierre'
                            }
                        </button>
                    </div>
                </LumaAccessiblePanel>
            </LumaAccessibleDialog>
        </>,
        document.body
    );
};

export default ModalCerrarSesion;

